import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import get from 'lodash.get';
import search from 'javascript-array-search';

// Selectors
import { selectWorkTypes } from '../../../../../../services/workTypes/selectors';
import { selectEntityTypes } from '../../../../../../services/entityTypes/selectors';

// Workspaces
import {
    makeSelectQueryStringValue,
    makeSelectQueryStringNumberValue
} from '../../../../../../workspaces/react-router-selectors';

// Utils
import orderBy from '../../../../../../utils/custom-orderBy';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectData = createDeepEqualSelector(selectWorkTypes, workTypesState =>
    Object.values(workTypesState)
);

const selectRelevantData = createDeepEqualSelector(selectData, dataState =>
    dataState.filter(value => !value.deletedAt)
);

const selectDenormalizedData = createDeepEqualSelector(
    [selectRelevantData, selectEntityTypes],
    (relevantDataState, entityTypesState) =>
        relevantDataState.map(value => {
            const { entityTypeId } = value;
            const entityType = get(entityTypesState, `${entityTypeId}`, {});
            return {
                ...value,
                entityType
            };
        })
);

const selectFilteredData = createDeepEqualSelector(
    [selectDenormalizedData, makeSelectQueryStringValue('search')],
    (denormalizedDataState, searchState) =>
        search(denormalizedDataState, searchState, [
            'name',
            'entityType.name',
            'payrollCode'
        ])
);

const selectDataLength = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState => filteredDataState.length
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredData, makeSelectQueryStringValue('sort')],
    (filteredDataState, sortState = 'name_ascending') => {
        const attributes = sortState.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';
        return orderBy(filteredDataState, propertyName, order);
    }
);

const selectPaginatedData = createDeepEqualSelector(
    [
        selectOrderedData,
        makeSelectQueryStringNumberValue('page'),
        makeSelectQueryStringNumberValue('count')
    ],
    (orderedDataState, pageState = 1, countState = 25) => {
        const begin = (pageState - 1) * countState;
        const end = pageState * countState;
        return orderedDataState.slice(begin, end);
    }
);

const selectIds = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState =>
        paginatedDataState && paginatedDataState.map(value => value.id)
);

export {
    selectData,
    selectDataLength,
    selectFilteredData,
    selectOrderedData,
    selectIds
};
